import type {
  PullRequestInvolvement,
  PullRequestListFilters,
  PullRequestListState,
} from "@afar-research/contracts";
import { createFileRoute } from "@tanstack/react-router";

import { PullRequestsWorkspace } from "../components/PullRequestsWorkspace";

export type PullRequestsWorkspaceSearch = {
  readonly project?: string;
  readonly environment?: string;
  readonly state?: PullRequestListState;
  readonly involvement?: PullRequestInvolvement;
  readonly q?: string;
  readonly sort?: "updated" | "oldest" | "title";
  readonly draft?: NonNullable<PullRequestListFilters["draft"]>;
  readonly review?: NonNullable<PullRequestListFilters["review"]>;
  readonly checks?: NonNullable<PullRequestListFilters["checks"]>;
  readonly author?: string;
  readonly labels?: string;
  readonly excludedLabels?: string;
  /**
   * The pull request whose detail the adjacent panel is showing. It is kept apart from the
   * filters so selecting one cannot silently narrow the list, and it is what a reload restores:
   * the panel's other tabs belong to the session, not to the address.
   */
  readonly selectedRepository?: string;
  readonly selectedNumber?: number;
  readonly selectedProject?: string;
  /** Two Environments can hold one project id, and two hosts one repository name. */
  readonly selectedEnvironment?: string;
  readonly selectedHost?: string;
};

function boundedString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim().slice(0, 200)
    : undefined;
}

/**
 * Reads the workspace address. Anything malformed is dropped rather than carried, so a stored
 * or hand-edited URL cannot address a pull request nobody asked for.
 */
export function validatePullRequestsWorkspaceSearch(
  raw: Record<string, unknown>,
): PullRequestsWorkspaceSearch {
  const project = boundedString(raw.project);
  const environment = boundedString(raw.environment);
  const q = boundedString(raw.q);
  const author = boundedString(raw.author);
  const labels = boundedString(raw.labels);
  const excludedLabels = boundedString(raw.excludedLabels);
  const selectedRepository = boundedString(raw.selectedRepository);
  const selectedProject = boundedString(raw.selectedProject);
  const selectedEnvironment = boundedString(raw.selectedEnvironment);
  const selectedHost = boundedString(raw.selectedHost);
  const selectedNumber =
    typeof raw.selectedNumber === "number" &&
    Number.isSafeInteger(raw.selectedNumber) &&
    raw.selectedNumber > 0
      ? raw.selectedNumber
      : typeof raw.selectedNumber === "string" && /^\d+$/u.test(raw.selectedNumber)
        ? Number.parseInt(raw.selectedNumber, 10)
        : undefined;
  return {
    ...(raw.sort === "oldest" || raw.sort === "title" ? { sort: raw.sort } : {}),
    state:
      raw.state === "all" || raw.state === "closed" || raw.state === "merged" ? raw.state : "open",
    involvement:
      raw.involvement === "reviewing" || raw.involvement === "authored" ? raw.involvement : "all",
    ...(project === undefined ? {} : { project }),
    ...(environment === undefined ? {} : { environment }),
    ...(q === undefined ? {} : { q }),
    ...(raw.draft === "only" || raw.draft === "hide" ? { draft: raw.draft } : {}),
    ...(raw.review === "approved" ||
    raw.review === "changes-requested" ||
    raw.review === "review-required" ||
    raw.review === "none"
      ? { review: raw.review }
      : {}),
    ...(raw.checks === "passing" || raw.checks === "failing" ? { checks: raw.checks } : {}),
    ...(author === undefined ? {} : { author }),
    ...(labels === undefined ? {} : { labels }),
    ...(excludedLabels === undefined ? {} : { excludedLabels }),
    // A half-stated selection addresses nothing, so it is dropped rather than restored as a
    // pull request nobody asked for.
    ...(selectedRepository !== undefined && selectedNumber !== undefined
      ? {
          selectedRepository,
          selectedNumber,
          ...(selectedProject === undefined ? {} : { selectedProject }),
          ...(selectedEnvironment === undefined ? {} : { selectedEnvironment }),
          ...(selectedHost === undefined ? {} : { selectedHost }),
        }
      : {}),
  };
}

export const Route = createFileRoute("/_chat/pull-requests")({
  validateSearch: validatePullRequestsWorkspaceSearch,
  component: PullRequestsWorkspace,
});
